// ============================================================================
//  demoData.js — the fictional world behind /demo.
//
//  Every person, goal, mile marker and note in here is invented. Nothing is
//  derived from real Mile Marker data. The demo never talks to Supabase, so a
//  prospect holding the /demo link can never reach real men's information.
//
//  Mile marker months are computed relative to "now" so the demo always looks
//  freshly used, no matter when it's opened.
// ============================================================================

function mk(offset) {
  // month key "YYYY-MM", offset months back from the current month
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function ts(offset) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - offset);
  d.setDate(15);
  return d.getTime();
}

const GROUP_A = "demo-group-a";
const GROUP_B = "demo-group-b";
const GROUP_C = "demo-group-c";

// Team C exists so the demo can SHOW that an admin is scoped rather than
// global: Pastor Dan leads A and B, and genuinely cannot see C.
export const DEMO_TEAMS = {
  [GROUP_A]: { id: GROUP_A, name: "Tuesday 6AM", max_members: 15 },
  [GROUP_B]: { id: GROUP_B, name: "Thursday Night", max_members: 15 },
  [GROUP_C]: { id: GROUP_C, name: "Saturday Sunrise", max_members: 15 },
};

// A team can have more than one leader; B shows that.
export const DEMO_LEADERS = {
  [GROUP_A]: ["demo-mike", "demo-dan"],
  [GROUP_B]: ["demo-nate", "demo-dan"],
  [GROUP_C]: ["demo-carl"],
};

// The three personas a prospect can view the app as.
export const DEMO_PERSONAS = [
  { id: "demo-marcus", label: "Marcus (Member)",    role: "mentee" },
  { id: "demo-mike",   label: "Mike (Team Admin)",  role: "mentor" },
  { id: "demo-dan",    label: "Pastor Dan (Admin)", role: "admin" },
  { id: "demo-greg",   label: "Greg (Super Admin)", role: "super_admin" },
];

export function buildDemoSeed() {
  const people = {
    "demo-greg": {
      id: "demo-greg", name: "Greg Sanderson", role: "super_admin",
      retreat: "Equilibrium Retreat", photo: "", group_id: null, is_suspended: false,
    },
    "demo-nate": {
      id: "demo-nate", name: "Nate Coleman", role: "mentor",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_B, is_suspended: false,
    },
    "demo-carl": {
      id: "demo-carl", name: "Carl Boyd", role: "mentor",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_C, is_suspended: false,
    },
    "demo-owen": {
      id: "demo-owen", name: "Owen Pratt", role: "mentee",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_C, is_suspended: false,
    },
    "demo-dan": {
      id: "demo-dan", name: "Pastor Dan Whitfield", role: "admin",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_A, is_suspended: false,
    },
    "demo-mike": {
      id: "demo-mike", name: "Mike Reyes", role: "mentor",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_A, is_suspended: false,
    },
    "demo-marcus": {
      id: "demo-marcus", name: "Marcus Bell", role: "mentee",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_A, is_suspended: false,
    },
    "demo-troy": {
      id: "demo-troy", name: "Troy Hendricks", role: "mentee",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_A, is_suspended: false,
    },
    "demo-sam": {
      id: "demo-sam", name: "Sam Okafor", role: "mentee",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_A, is_suspended: false,
    },
    "demo-luis": {
      id: "demo-luis", name: "Luis Herrera", role: "mentee",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_B, is_suspended: false,
    },
    "demo-pete": {
      id: "demo-pete", name: "Pete Lawson", role: "mentee",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_B, is_suspended: false,
    },
    "demo-ray": {
      id: "demo-ray", name: "Ray Thompson", role: "mentee",
      retreat: "Equilibrium Retreat", photo: "", group_id: GROUP_B, is_suspended: true,
    },
  };

  const goals = {
    "demo-marcus": [
      { id: "dg-m1", f: "faith", title: "Read through the Gospels before year end",
        smart: { s: "Read all four Gospels with a short written reflection per chapter",
          m: "3 chapters per week", a: "Morning reading before the house wakes up",
          r: "Build a daily habit of being in the Word", t: "By Dec 31" },
        note: "Reflections shared with the Tuesday group.", photos: [] },
      { id: "dg-m2", f: "family", title: "Weekly one-on-one with each kid",
        smart: { s: "30 uninterrupted minutes with each of my two kids",
          m: "2 one-on-ones per week", a: "Calendar blocked Saturday mornings",
          r: "Be present, not just around", t: "Weekly, ongoing" },
        note: "", photos: [] },
      { id: "dg-m3", f: "friends", title: "Monthly breakfast with an EQ brother",
        smart: { s: "Sit down with a different brother from the retreat each month",
          m: "1 breakfast per month", a: "Rotate through the roster",
          r: "Keep the retreat relationships alive", t: "Monthly, ongoing" },
        note: "", photos: [] },
      { id: "dg-m4", f: "fitness", title: "Run a 10K in under an hour",
        smart: { s: "Train up to a sub-60 10K", m: "3 runs per week, long run Saturdays",
          a: "Couch-to-10K plan, week 6 of 12", r: "Energy to keep up with my kids",
          t: "Race day Oct 12" },
        note: "", photos: [] },
      { id: "dg-m5", private_details: true, f: "finances", title: "Build a 3-month emergency fund",
        smart: { s: "Save three months of expenses in a separate account",
          m: "$400 auto-transfer per paycheck", a: "Cut two streaming services and eating out",
          r: "Stop living paycheck to paycheck", t: "By next June" },
        note: "", photos: [] },
    ],
    "demo-troy": [
      { id: "dg-t1", f: "faith", title: "Pray with my wife every night",
        smart: { s: "End each day praying together", m: "7 nights per week",
          a: "Before phones, right after the kids are down", r: "Lead spiritually at home",
          t: "Nightly, ongoing" },
        note: "", photos: [] },
      { id: "dg-t2", f: "fitness", title: "Drop 20 lbs by the next retreat",
        smart: { s: "Lose 20 pounds", m: "Weigh-in photo before each group meeting",
          a: "1-2 lbs per week, tracking meals", r: "Show up stronger next year",
          t: "By next retreat" },
        note: "", photos: [] },
      { id: "dg-t3", f: "finances", title: "Kill the last credit card balance",
        smart: { s: "Pay off remaining $3,800 card balance", m: "$500 per month minimum",
          a: "Side jobs on two Saturdays a month", r: "Free our family from the interest",
          t: "In 8 months" },
        note: "", photos: [] },
    ],
    "demo-sam": [
      { id: "dg-s1", f: "faith", title: "Serve once a month at church",
        smart: { s: "Join the setup team one Sunday per month", m: "1 serve per month",
          a: "First Sunday each month", r: "Move from attending to contributing",
          t: "Monthly, ongoing" },
        note: "", photos: [] },
      { id: "dg-s2", f: "friends", title: "Call one old friend every week",
        smart: { s: "Reconnect with friends I've let drift", m: "1 call per week",
          a: "Sunday evening drive home", r: "Don't do life alone",
          t: "Weekly, ongoing" },
        note: "", photos: [] },
      { id: "dg-s3", f: "family", title: "Date night twice a month",
        smart: { s: "Take my wife out, phones down", m: "2 per month",
          a: "Sitter swap with the Bells", r: "Invest in the marriage first",
          t: "Ongoing" },
        note: "", photos: [] },
    ],
    "demo-luis": [
      { id: "dg-l1", f: "fitness", title: "Morning walks, 5 days a week",
        smart: { s: "30-minute walk before work", m: "5 walks per week",
          a: "Out the door by 6:15", r: "Doctor's orders after the physical",
          t: "Weekly, ongoing" },
        note: "", photos: [] },
      { id: "dg-l2", f: "faith", title: "Finish the men's study book",
        smart: { s: "Work through the group study book", m: "1 chapter per week",
          a: "Thursday group pace", r: "Stay engaged with the group",
          t: "By the final session" },
        note: "", photos: [] },
    ],
    "demo-pete": [
      { id: "dg-p1", f: "family", title: "Coach my son's fall team",
        smart: { s: "Assistant coach the U10 season", m: "2 practices + 1 game per week",
          a: "Shifted my work schedule", r: "Be in his world, not just watching it",
          t: "Through November" },
        note: "", photos: [] },
      { id: "dg-p2", f: "finances", title: "Meet with a financial planner",
        smart: { s: "Get a real retirement plan on paper", m: "1 completed plan",
          a: "Referral from Mike", r: "Stop guessing about the future",
          t: "By end of quarter" },
        note: "", photos: [] },
    ],
    "demo-mike": [
      { id: "dg-k1", f: "faith", title: "Mentor check-ins that go deeper",
        smart: { s: "One real question per man per meeting, follow up mid-month",
          m: "5 mid-month texts per cycle", a: "Reminders set after each meeting",
          r: "Mentoring is more than the meeting", t: "Monthly, ongoing" },
        note: "", photos: [] },
      { id: "dg-k2", f: "fitness", title: "Ride 100 miles a month",
        smart: { s: "Back on the bike consistently", m: "100 miles per month",
          a: "Two morning rides per week", r: "Practice what I preach to the group",
          t: "Monthly, ongoing" },
        note: "", photos: [] },
    ],
    "demo-dan": [
      { id: "dg-d1", f: "faith", title: "Sabbath actually means rest",
        smart: { s: "A real day off each week, no sermon prep", m: "1 full day per week",
          a: "Fridays, calendar blocked", r: "Model the rhythm I teach",
          t: "Weekly, ongoing" },
        note: "", photos: [] },
    ],
  };

  const marks = {
    // Marcus - a strong, believable arc
    "dg-m1": [
      { month: mk(3), pct: 20, rag: "amber", note: "Slow start, missed two mornings a week.", blockers: "Staying up too late.", commitment: "Lights out by 10:30 on weeknights.", commitment_kept: true, ts: ts(3) },
      { month: mk(2), pct: 45, rag: "green", note: "Finished Matthew, into Mark. The 10:30 rule is working.", blockers: "", commitment: "Finish Mark and start journaling one line per chapter.", commitment_kept: true, ts: ts(2) },
      { month: mk(1), pct: 60, rag: "green", note: "Mark done. Reflections are the best part of my day now.", blockers: "", commitment: "Read Luke 1-12 and share one reflection with the group.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-m2": [
      { month: mk(2), pct: 50, rag: "amber", note: "Got 1 of 2 most weeks. Saturdays keep getting eaten.", blockers: "Yard work and errands creep in.", commitment: "Move the second one-on-one to Wednesday dinner.", commitment_kept: true, ts: ts(2) },
      { month: mk(1), pct: 75, rag: "green", note: "Wednesday slot stuck. My daughter now reminds ME.", blockers: "", commitment: "Let each kid pick the activity this month.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-m3": [
      { month: mk(1), pct: 100, rag: "green", note: "Breakfast with Troy. Two hours, could have gone four.", blockers: "", commitment: "Sam is next, already texted him.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-m4": [
      { month: mk(2), pct: 30, rag: "amber", note: "Week 4 of the plan. Knees complaining.", blockers: "Old shoes.", commitment: "Get fitted for real running shoes.", commitment_kept: true, ts: ts(2) },
      { month: mk(1), pct: 55, rag: "green", note: "New shoes fixed it. Long run at 5 miles now.", blockers: "", commitment: "Hit the 10K distance at any pace before race day.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-m5": [
      { month: mk(3), pct: 15, rag: "green", note: "Auto-transfer set up. First $800 in.", blockers: "", commitment: "Don't touch it. Cancel the two subscriptions.", commitment_kept: true, ts: ts(3) },
      { month: mk(1), pct: 35, rag: "green", note: "On pace. $2,100 saved.", blockers: "", commitment: "Put the tax refund straight in.", commitment_kept: null, ts: ts(1) },
    ],

    // Troy - honest struggle, the app's accountability story
    "dg-t1": [
      { month: mk(2), pct: 40, rag: "amber", note: "Maybe 3 nights a week. Awkward at first, getting easier.", blockers: "We both crash after the kids go down.", commitment: "Pray right after dinner instead of bedtime.", commitment_kept: false, ts: ts(2) },
      { month: mk(1), pct: 45, rag: "amber", note: "Didn't move the time like I said. Being honest here.", blockers: "Habit inertia.", commitment: "Same commitment again. Dinner table, hands held, 60 seconds.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-t2": [
      { month: mk(3), pct: 25, rag: "green", note: "5 down. Tracking every meal.", blockers: "", commitment: "Keep the streak. No fast food on work trips.", commitment_kept: true, ts: ts(3) },
      { month: mk(2), pct: 40, rag: "green", note: "8 down total.", blockers: "", commitment: "Add the Saturday walk with Luis.", commitment_kept: true, ts: ts(2) },
      { month: mk(1), pct: 45, rag: "amber", note: "Plateaued. 9 down.", blockers: "Late-night snacking is back.", commitment: "Kitchen closed after 8pm. Told my wife to hold me to it.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-t3": [
      { month: mk(1), pct: 30, rag: "green", note: "$1,100 paid so far. Two Saturday jobs done.", blockers: "", commitment: "$600 this month - stretch goal.", commitment_kept: null, ts: ts(1) },
    ],

    // Sam
    "dg-s1": [
      { month: mk(2), pct: 100, rag: "green", note: "Served first Sunday. Met three guys I'd never talked to.", blockers: "", commitment: "Bring Pete along next month.", commitment_kept: true, ts: ts(2) },
      { month: mk(1), pct: 100, rag: "green", note: "Pete came. We're the coffee station guys now.", blockers: "", commitment: "Keep the streak alive.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-s2": [
      { month: mk(1), pct: 60, rag: "amber", note: "3 of 4 weeks. College roommate talked for an hour.", blockers: "Sunday drives don't happen every week.", commitment: "Backup slot: Tuesday lunch.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-s3": [
      { month: mk(1), pct: 50, rag: "amber", note: "One date night, not two. Sitter fell through.", blockers: "Sitter availability.", commitment: "Set up the sitter swap with Marcus's family.", commitment_kept: null, ts: ts(1) },
    ],

    // Luis
    "dg-l1": [
      { month: mk(2), pct: 70, rag: "green", note: "4-5 walks most weeks. Down a belt notch.", blockers: "", commitment: "Add ten minutes to two walks a week.", commitment_kept: true, ts: ts(2) },
      { month: mk(1), pct: 80, rag: "green", note: "Best month yet. Blood pressure down at the follow-up.", blockers: "", commitment: "Invite Troy on Saturdays.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-l2": [
      { month: mk(1), pct: 40, rag: "amber", note: "A chapter behind the group.", blockers: "Work travel.", commitment: "Catch up on the flight Thursday.", commitment_kept: null, ts: ts(1) },
    ],

    // Pete
    "dg-p1": [
      { month: mk(1), pct: 65, rag: "green", note: "Season underway. He asked me to warm him up before games.", blockers: "", commitment: "Don't miss the away games.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-p2": [
      { month: mk(1), pct: 10, rag: "red", note: "Still haven't booked it. No excuse.", blockers: "Keep putting it off.", commitment: "Call Mike's guy by Friday. Group, ask me about this.", commitment_kept: null, ts: ts(1) },
    ],

    // Mike
    "dg-k1": [
      { month: mk(1), pct: 80, rag: "green", note: "Mid-month texts sent to all five. Two turned into calls.", blockers: "", commitment: "Grab lunch with Pete - the planner goal needs a push.", commitment_kept: null, ts: ts(1) },
    ],
    "dg-k2": [
      { month: mk(1), pct: 90, rag: "green", note: "92 miles. Weather cooperated.", blockers: "", commitment: "Break 100 this month.", commitment_kept: null, ts: ts(1) },
    ],

    // Pastor Dan
    "dg-d1": [
      { month: mk(1), pct: 70, rag: "amber", note: "Took 3 of 4 Fridays. One got eaten by a funeral - some weeks are like that.", blockers: "", commitment: "Guard the next four. Phone on do-not-disturb.", commitment_kept: null, ts: ts(1) },
    ],
  };

  // Teams and leaders are cloned so demo mutations (rename, assign, move)
  // never write back into the module-level constants.
  const groups = {};
  for (const [id, t] of Object.entries(DEMO_TEAMS)) groups[id] = { ...t };
  const leaders = {};
  for (const [id, ids] of Object.entries(DEMO_LEADERS)) leaders[id] = [...ids];

  return { people, goals, marks, groups, leaders };
}
